import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { AppDispatch } from '../../state';
import i18n from '../../utils/i18n';
import I18n from '../../utils/i18n';
import GridCol from '../../ui/GridCol';
import { GridItemAlign } from '../../ui/GridCol/GridCol';
import Input from '../../ui/Input';
import GridRow from '../../ui/GridRow';
import Body from '../../ui/Body/body';
import ToggleButton from '../../ui/ToggleButton/ToggleButton';
import { IDeliverForm } from './types';
import { parseHistoryState } from './utils/parseHistoryState';
import DeliveryConditions from './modules/deliveryConditions';
import sCommon from '../../utils/common.module.scss';
import Button from '../../ui/Button';
import { ButtonVariationEnum } from '../../ui/Button/types';
import { getDirtyFormValues } from '../../utils/form';
import { setNotificationError, setNotificationSuccess } from '../../state/reducers/notification';
import { IDelivery } from '../../utils/types';
import { FetchApi } from '../../utils/fetch/fetchApi';
import { paths, pathsApi } from '../../router/paths';

const DeliveryNew = () => {
    const dispatch = useDispatch<AppDispatch>();
    const defaultValues = parseHistoryState();
    const {
        control,
        reset,
        handleSubmit,
        formState: { dirtyFields },
    } = useForm<IDeliverForm>({ defaultValues });
    const navigate = useNavigate();
    const [isLoading, toggleLoading] = useState<boolean>(false);

    const submit = handleSubmit(async (data) => {
        toggleLoading(true);
        const payload = defaultValues?.id ? getDirtyFormValues<IDeliverForm>({ formFields: data, dirtyFields }) : data;

        if (!payload) {
            dispatch(setNotificationError(i18n.general.noChanges));
            toggleLoading(false);
            return;
        }

        const serializedData: Partial<IDelivery> & { visible?: boolean } = {
            id: data.id,
            options: payload.options?.map((item) => item.value),
            name: payload.name,
            price: payload.price,
            visible: payload.visible,
        };

        const response = await FetchApi.post<IDelivery>(pathsApi.deliverySave, serializedData);
        if (response?.id) {
            dispatch(
                setNotificationSuccess(
                    defaultValues?.id ? i18n.general.updatedSuccessful : i18n.general.successFullyAdded,
                ),
            );
            navigate(paths.delivery + response.id, { state: { delivery: response } });
        } else {
            toggleLoading(false);
        }
    });

    return (
        <Body title={defaultValues?.id ? i18n.delivery.updateDelivery : i18n.menu.deliveryNew}>
            <GridRow gutter={8}>
                <GridCol col={4} align={GridItemAlign.TOP}>
                    <Controller
                        name={'name'}
                        control={control}
                        rules={{ required: true }}
                        defaultValue={''}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                value={value}
                                onChange={onChange}
                                label={i18n.general.name}
                                infoMessage={i18n.product.nameHint}
                                error={!!error}
                            />
                        )}
                    />
                </GridCol>
                <GridCol col={4} align={GridItemAlign.TOP}>
                    <Controller
                        name={'price'}
                        control={control}
                        rules={{ required: true }}
                        defaultValue={0}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                value={value}
                                onChange={onChange}
                                isCurrency
                                type="number"
                                label={i18n.price.mainPrice}
                                error={!!error}
                            />
                        )}
                    />
                </GridCol>
                <GridCol col={12}>
                    <Controller
                        name={'visible'}
                        control={control}
                        defaultValue={true}
                        render={({ field: { onChange, value } }) => (
                            <ToggleButton value={value} onClick={onChange} label={i18n.product.isVisible} />
                        )}
                    />
                </GridCol>
                <GridCol col={12}>
                    <DeliveryConditions control={control} />
                </GridCol>
                <GridCol col={12} className={sCommon.buttonWrapper}>
                    <Button onClick={submit} isLoading={isLoading}>
                        {I18n.general.save}
                    </Button>
                    <Button variation={ButtonVariationEnum.SOFT} onClick={() => reset()}>
                        {I18n.general.reset}
                    </Button>
                </GridCol>
            </GridRow>
        </Body>
    );
};

export default DeliveryNew;
